import React, { useContext, useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Config, I18n, I18nContext } from "@deporvillage/utils-library-component";
import Picture from "@deporvillage/picture-component";

import styles from "./CountriesList.module.scss";

const normalize = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const CountriesSearch = ({ onChange }) => {
    const { translate } = useContext(I18nContext);
    return (
        <div className={ styles["countries-input-search"] }>
            <input
                data-testid={ "countries-input-search" }
                type="text"
                autoComplete="off"
                placeholder={ translate("header.countries.ship_to") }
                onChange={ onChange }
            />
            <span className={`${styles['icon']} icon icon-search`}/>
        </div>
    );
};
CountriesSearch.propTypes = {
    onChange: PropTypes.func
};

const ListOfCountries = ({ countries }) => (
    <div className={ styles["countries-list"] } data-testid={ "countries-list-list-of-countries" }>
        { countries.map(c => (
            <div key={ c.code } className={ styles["country-item"] } data-testid={ "country-item" }>
                <span className={ styles["country-nav-link"] } data-currency={ c.currency_code } data-location={ c.location }>
                    <Picture
                        className={ styles['flag-icon'] }
                        src={ `static/flags/${ c.code.toLowerCase() }.png` }
                        width={ 16 }
                        height={ 12 }
                        name={ `${ c.code } - ${ c.countryName }`}
                    />
                    { c.countryName }
                </span>
            </div>
        )) }
    </div>
);
ListOfCountries.propTypes = {
    countries: PropTypes.array,
};

const CountriesList = () => {

    const { translate } = useContext(I18nContext);
    const { api, country, url } = useContext(Config);

    const [countries, setCountries] = useState(null);
    const [filterCountries, setFilterCountries] = useState(null);
    const [countriesListBox, setCountriesListBox] = useState(false);

    const wrapperRef = useRef(null);

    const escAndClickOutside = useCallback(e => {
        if (e.keyCode === 27) {
            setCountriesListBox(false);
        } else if (
            wrapperRef.current &&
            !wrapperRef.current.contains(e.target)
        ) {
            setCountriesListBox(false);
        }
    }, []);

    const handleToggle = () => {

        setCountriesListBox(!countriesListBox);
        if (countries) return;

        /* eslint-disable-next-line no-undef */
        fetch(`${api}/countries`)
            .then(countries => countries.json())
            /* istanbul ignore next */
            .catch(() => {})
            /* istanbul ignore next */
            .then(ans => {
                if (ans) {
                    let countries = ans.map(c => ({
                        ...c,
                        countryName: translate(
                            `countries.${ c.code.toLowerCase() }`
                        ),
                        /* eslint-disable-next-line no-undef */
                        location: btoa(
                            `${ url }/directory/currency/change/currency/${ c.currency_code }/country/${ c.code }/`
                        )
                    }))
                        .filter(({ is_frontend_active }) => is_frontend_active)
                        .sort(/* istanbul ignore next */ (a, b) => a.countryName < b.countryName ? -1 : 1);

                    setCountries(countries);
                    setFilterCountries(countries);

                    /* eslint-disable no-undef */
                    window.dispatchEvent(new CustomEvent('countries:loaded', {
                        detail: {
                            elem: wrapperRef.current,
                            uenc: btoa(window.location.href),
                        }
                    }));
                    /* eslint-enable no-undef */
                }
            });
    };

    const handleChange = (e) => {
        if (!filterCountries) return;
        let val = normalize(e.target.value);
        setFilterCountries(countries.filter(x => normalize(x.countryName).match(val)));
    };

    useEffect(() => {
        (filterCountries || countriesListBox) && wrapperRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    }, [countriesListBox, filterCountries]);

    useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener("mousedown", escAndClickOutside, false);
        window.addEventListener("keydown", escAndClickOutside, false);
        /* eslint-enable no-undef */
        return () => {
            // eslint-disable-next-line no-undef
            window.removeEventListener("mousedown", escAndClickOutside);
            // eslint-disable-next-line no-undef
            window.removeEventListener("keydown", escAndClickOutside);
        };
    }, []);

    return (
        <div className={ `${styles['countries-list-component']} ${ countriesListBox ? styles['is-open'] : '' }` } ref={ wrapperRef }>
            <div data-testid={ `countries-list-get-countries` } className={ styles['country-to-ship'] } onClick={ handleToggle }>
                <span className={ styles['ship-to']}>
                    <I18n>header.countries.ship_to</I18n>
                </span>
                <I18n>{ `countries.${ country.code.toLowerCase() }` }</I18n>
            </div>
            <div className={ `${ styles['countries-list-box'] } ${ countriesListBox ? styles['visible'] : '' }` }>
                <CountriesSearch onChange={ handleChange }/>
                { filterCountries ? <ListOfCountries countries={ filterCountries } /> : <div className={ styles['countries-loader'] }/> }
            </div>
        </div>
    );
};

const CountriesListDropDown = () => (
    <div className={ styles['countries-list-drop-down-component'] }>
        <CountriesList/>
    </div>
);

export default CountriesList;
export { CountriesListDropDown };
